import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { searchFashion } from '../api/rakuten_service';
import { useNavigationStack } from '../contexts/NavigationContext';
import './Search.css'

const Search = () => {
  const [imagePreview, setImagePreview] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)
  const navigate = useNavigate()
  const { navigateWithTransition } = useNavigationStack();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Compress and resize image to ensure consistent quality across mobile/desktop
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          // Calculate new dimensions (max 1024px on longest side)
          const MAX_SIZE = 1024
          let width = img.width
          let height = img.height

          if (width > height) {
            if (width > MAX_SIZE) {
              height = Math.round((height * MAX_SIZE) / width)
              width = MAX_SIZE
            }
          } else {
            if (height > MAX_SIZE) {
              width = Math.round((width * MAX_SIZE) / height)
              height = MAX_SIZE
            }
          }

          // Create canvas and draw resized image
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, width, height)

          // Convert to blob with quality compression
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Convert blob to data URL for preview
                const compressedReader = new FileReader()
                compressedReader.onloadend = () => {
                  resolve(compressedReader.result)
                }
                compressedReader.readAsDataURL(blob)
              } else {
                reject(new Error('Failed to compress image'))
              }
            },
            'image/jpeg',
            0.85 // 85% quality
          )
        }
        img.onerror = () => reject(new Error('Failed to load image'))
        img.src = e.target.result
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsDataURL(file)
    })
  }

  const handleFileSelect = async (file) => {
    if (file && file.type.startsWith('image/')) {
      try {
        // Compress image for consistent quality across mobile/desktop
        const compressedImage = await compressImage(file)
        setImagePreview(compressedImage)
      } catch (error) {
        console.error('Image compression failed:', error)
        // Fallback to original if compression fails
        const reader = new FileReader()
        reader.onloadend = () => {
          setImagePreview(reader.result)
        }
        reader.readAsDataURL(file)
      }
    }
  }


  const handleFileInput = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
    setShowMobileMenu(false)
  }

  const handleUploadClick = () => {
    // Check if mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

    if (isMobile) {
      // Show action sheet on mobile
      setShowMobileMenu(true)
    } else {
      // Desktop: directly open file picker
      fileInputRef.current?.click()
    }
  }

  const handleGalleryClick = () => {
    fileInputRef.current?.click()
    setShowMobileMenu(false)
  }

  const handleCameraClickMobile = () => {
    cameraInputRef.current?.click()
    setShowMobileMenu(false)
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleCameraClick = async () => {
    try {
      // Check if camera is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        // Fallback: trigger file input with camera capture
        if (fileInputRef.current) {
          fileInputRef.current.setAttribute('capture', 'environment')
          fileInputRef.current.click()
        }
        return
      }
      
      // Try to access camera
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment' // Use back camera on mobile
        } 
      })
      
      // Create video element for camera preview
      const video = document.createElement('video')
      video.srcObject = stream
      video.autoplay = true
      video.playsInline = true
      
      // Create canvas to capture image
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      // Show camera preview modal
      const modal = document.createElement('div')
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.9);
        z-index: 10000;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 20px;
      `
      
      const videoContainer = document.createElement('div')
      videoContainer.style.cssText = `
        max-width: 90%;
        max-height: 70%;
        border-radius: 20px;
        overflow: hidden;
      `
      videoContainer.appendChild(video)
      modal.appendChild(videoContainer)
      
      const captureBtn = document.createElement('button')
      captureBtn.textContent = 'ÃƒÆ’Ã‚Â°Ãƒâ€¦Ã‚Â¸ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã¢â‚¬Å“ÃƒÆ’Ã¢â‚¬Å¡Ãƒâ€šÃ‚Â¸ Capture Photo'
      captureBtn.style.cssText = `
        padding: 15px 40px;
        background: linear-gradient(135deg, #FF1493 0%, #00CED1 100%);
        color: white;
        border: none;
        border-radius: 50px;
        font-size: 1.2rem;
        font-weight: 600;
        cursor: pointer;
        box-shadow: 0 4px 15px rgba(255, 20, 147, 0.3);
      `
      
      const cancelBtn = document.createElement('button')
      cancelBtn.textContent = 'Cancel'
      cancelBtn.style.cssText = `
        padding: 10px 30px;
        background: rgba(255, 255, 255, 0.2);
        color: white;
        border: none;
        border-radius: 50px;
        font-size: 1rem;
        cursor: pointer;
      `
      
      // Create gallery button (bottom-left thumbnail)
      const galleryBtn = document.createElement('button')
      galleryBtn.style.cssText = `
        position: absolute;
        bottom: 30px;
        left: 30px;
        width: 50px;
        height: 50px;
        border-radius: 10px;
        border: 2px solid rgba(255, 255, 255, 0.5);
        background: rgba(0, 0, 0, 0.5);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        padding: 0;
      `

      // Gallery icon (image placeholder)
      const galleryIcon = document.createElement('div')
      galleryIcon.innerHTML = `
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
      `
      galleryBtn.appendChild(galleryIcon)

      // Hidden file input for gallery (no capture attribute)
      const galleryInput = document.createElement('input')
      galleryInput.type = 'file'
      galleryInput.accept = 'image/*'
      galleryInput.style.display = 'none'
      modal.appendChild(galleryInput)

      // Handle gallery selection
      galleryInput.onchange = (e) => {
        const file = e.target.files?.[0]
        if (file) {
          // Cleanup camera
          stream.getTracks().forEach(track => track.stop())
          document.body.removeChild(modal)
          // Process the gallery image
          handleFileSelect(file)
        }
      }

      galleryBtn.onclick = () => {
        galleryInput.click()
      }

      modal.appendChild(galleryBtn)

      const buttonContainer = document.createElement('div')
      buttonContainer.style.cssText = 'display: flex; gap: 15px;'
      buttonContainer.appendChild(captureBtn)
      buttonContainer.appendChild(cancelBtn)
      modal.appendChild(buttonContainer)
      
      document.body.appendChild(modal)
      
      // Handle capture
      captureBtn.onclick = () => {
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        ctx.drawImage(video, 0, 0)
        
        canvas.toBlob((blob) => {
          const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' })
          handleFileSelect(file)
        }, 'image/jpeg', 0.9)
        
        // Cleanup
        stream.getTracks().forEach(track => track.stop())
        document.body.removeChild(modal)
      }
      
      // Handle cancel
      cancelBtn.onclick = () => {
        stream.getTracks().forEach(track => track.stop())
        document.body.removeChild(modal)
      }
      
      // Auto-fit video
      video.onloadedmetadata = () => {
        video.style.width = '100%'
        video.style.height = 'auto'
      }
      
    } catch (error) {
      console.error('Error accessing camera:', error)
      // Fallback to file input with camera
      if (fileInputRef.current) {
        fileInputRef.current.setAttribute('capture', 'environment')
        fileInputRef.current.click()
      } else {
        alert('Unable to access camera. Please use "Upload Image" instead.')
      }
    }
  }

  const handleSearch = async () => {
    if (!imagePreview) return;

    // Navigate to analyzing page immediately with smooth transition
    navigateWithTransition('/analyzing', {
      state: { image: imagePreview },
      replace: false,
      direction: 'forward'
    });
  };

  const handleRemoveImage = () => {
    setImagePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="search-page">
      <div className="search-container">
        <motion.div
          className="search-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="search-title">Visual Search</h1>
          <p className="search-slogan">
            One photo. Every outfit.
          </p>
        </motion.div>

        <motion.div
          className="search-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {!imagePreview ? (
            <div
              className={`upload-area ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="upload-content">
                <h3 className="upload-title">Snap it here</h3>
                <p className="upload-subtitle">Upload your photo or take a picture with your camera</p>
                <div className="upload-buttons">
                  {/* Gallery input (no capture attribute) */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInput}
                    className="file-input"
                    id="file-input"
                    aria-label="Upload image from gallery"
                    style={{ display: 'none' }}
                  />
                  {/* Camera input (with capture attribute) */}
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileInput}
                    className="file-input"
                    id="camera-input"
                    aria-label="Take photo with camera"
                    style={{ display: 'none' }}
                  />
                  <button
                    className="upload-button"
                    onClick={handleUploadClick}
                    aria-label="Upload image"
                  >
                    Upload Image
                  </button>
                  <button
                    className="camera-button-inline"
                    onClick={handleCameraClick}
                    aria-label="Use camera"
                  >
                    Take Photo
                  </button>
                </div>

                {/* Text Search Alternative */}
                <div className="text-search-link-section">
                  <span className="text-search-divider">or</span>
                  <Link to="/text-search" className="text-search-link">
                    Search by text instead
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="image-preview-container">
              <div className="image-preview-wrapper">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="image-preview"
                />
                <button
                  className="remove-image-btn"
                  onClick={handleRemoveImage}
                  aria-label="Remove image"
                >
                  ÃƒÆ’Ã†â€™ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â
                </button>
              </div>
              <div className="preview-actions">
                <button
                  className="btn btn-secondary"
                  onClick={handleRemoveImage}
                >
                  Change Image
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSearch}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <span className="spinner"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      Search Fashion
                      <span className="btn-arrow">ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

        </motion.div>

      </div>

      {/* Mobile Action Sheet */}
      {showMobileMenu && (
        <motion.div
          className="mobile-menu-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowMobileMenu(false)}
        >
          <motion.div
            className="mobile-menu-sheet"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mobile-menu-handle"></div>
            <h3 className="mobile-menu-title">Choose an option</h3>
            <div className="mobile-menu-options">
              <button
                className="mobile-menu-option"
                onClick={handleGalleryClick}
              >
                <span className="mobile-menu-icon">ÃƒÆ’Ã‚Â°Ãƒâ€¦Ã‚Â¸ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Å“Ãƒâ€šÃ‚Â¼</span>
                <span className="mobile-menu-text">Choose from Gallery</span>
              </button>
              <button
                className="mobile-menu-option"
                onClick={handleCameraClickMobile}
              >
                <span className="mobile-menu-icon">ÃƒÆ’Ã‚Â°Ãƒâ€¦Ã‚Â¸ÃƒÂ¢Ã¢â€šÂ¬Ã…â€œÃƒâ€šÃ‚Â¸</span>
                <span className="mobile-menu-text">Take Photo</span>
              </button>
              <button
                className="mobile-menu-option mobile-menu-cancel"
                onClick={() => setShowMobileMenu(false)}
              >
                <span className="mobile-menu-text">Cancel</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}

export default Search

